import { createAsyncThunk } from '@reduxjs/toolkit';
import { classToPlain } from 'class-transformer';

import { AppError } from '@vk-hr-tek/core/error';
import { FormatService } from '@vk-hr-tek/core/format';
import {
  setDownloadWaiting,
  pollDownloadStatus,
} from '@vk-hr-tek/core/download-notification';

import {
  CreateEventBatchDocumentZipResponse,
  EventXlsxResponse,
  EventPdfResponse,
  CreateEventBatchDocumentZipRequest,
  EventsService,
  EventListRequestFilters,
  RequestEventDocumentDocxResponse,
  RequestEventDocumentDocxFileParams,
} from '@app/gen/events';

import { ThunkExtra } from '../../../app/store';
import { EventsDownloadService, EventsListService } from '../../services';
import { GetEventsPdfDto, StartDownloadXlsxDto } from '../../dto';
import { setFilters } from '../actions';
import { EventsWithRootState } from '../../slice/events.state';

interface BatchDownloadStartDto extends CreateEventBatchDocumentZipRequest {
  totalCount?: number;

  filters?: EventListRequestFilters;
}

export const startDownload = createAsyncThunk<
  CreateEventBatchDocumentZipResponse,
  BatchDownloadStartDto,
  ThunkExtra<EventsWithRootState>
>(
  'events/startBatchDownload',
  async (
    batchDownloadStartDto: BatchDownloadStartDto,
    { rejectWithValue, dispatch, extra: { inject } },
  ) => {
    try {
      const service = inject(EventsDownloadService);
      const eventService = inject(EventsService);

      const { totalCount, filters } = batchDownloadStartDto;

      let downloadId;
      if (totalCount) {
        const result = await eventService.operationEvents({
          body: {
            ...(filters && { filters }),
            operation: 'batch_download',
          },
        });

        downloadId = result.download_id || '';
      } else {
        const result = await service.startDownload(batchDownloadStartDto);

        downloadId = result.download_id;
      }

      await dispatch(
        setDownloadWaiting({
          id: downloadId,
          count: totalCount || batchDownloadStartDto.event_ids.length,
        }),
      );

      dispatch(
        pollDownloadStatus({
          download_id: downloadId,
        }),
      );

      return {
        download_id: downloadId,
      };
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const startDownloadXlsxByEvents = createAsyncThunk<
  EventXlsxResponse,
  StartDownloadXlsxDto,
  ThunkExtra<EventsWithRootState>
>(
  'events/startDownloadXlsxByEvents',
  async (
    startDownloadXlsxDto: StartDownloadXlsxDto,
    { rejectWithValue, getState, dispatch, extra: { inject } },
  ) => {
    try {
      const service = inject(EventsDownloadService);
      const eventsService = inject(EventsListService);
      const format = inject(FormatService);

      const state = getState().events;

      const { total } = state;

      let filters = state.filters;

      if (!filters.id || !filters.data) {
        filters = {
          data: await eventsService.getFilters(),
          id: '',
        };

        dispatch(setFilters(filters));
      }

      const result = await service.startDownloadXlsx(
        startDownloadXlsxDto,
        filters.data || [],
      );

      const formattedTotal = format.pluralize(total, [
        'заявка',
        'заявки',
        'заявок',
      ]);

      await dispatch(
        setDownloadWaiting({
          id: result.download_id,
          count: total,
          text: {
            loading: `Создаем файл (${formattedTotal})`,
            complete: `Файл создан (${formattedTotal})`,
            failed: 'Ошибка создания файла',
          },
        }),
      );

      dispatch(pollDownloadStatus(result));

      return result;
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const startDownloadXlsxByDocuments = createAsyncThunk<
  EventXlsxResponse,
  StartDownloadXlsxDto,
  ThunkExtra<EventsWithRootState>
>(
  'events/startDownloadXlsxByDocuments',
  async (
    startDownloadXlsxDto: StartDownloadXlsxDto,
    { rejectWithValue, getState, dispatch, extra: { inject } },
  ) => {
    try {
      const service = inject(EventsDownloadService);
      const eventsService = inject(EventsListService);
      const format = inject(FormatService);

      const state = getState().events;

      const { total, totalDocuments } = state;

      let filters = state.filters;

      if (!filters.id || !filters.data) {
        filters = {
          data: await eventsService.getFilters(),
          id: '',
        };

        dispatch(setFilters(filters));
      }

      const result = await service.startDownloadXlsx(
        startDownloadXlsxDto,
        filters.data || [],
        true,
      );

      const formattedTotal = format.pluralize(total, [
        'заявка',
        'заявки',
        'заявок',
      ]);
      const formattedTotalDocuments = format.pluralize(totalDocuments || 0, [
        'документ',
        'документа',
        'документов',
      ]);

      await dispatch(
        setDownloadWaiting({
          id: result.download_id,
          count: total,
          text: {
            loading: `Создаем файл (${formattedTotal}${
              totalDocuments ? `, ${formattedTotalDocuments}` : ''
            })`,
            complete: `Файл создан  (${formattedTotal}${
              totalDocuments ? `, ${formattedTotalDocuments}` : ''
            })`,
            failed: 'Ошибка создания файла',
          },
        }),
      );

      dispatch(pollDownloadStatus(result));

      return result;
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const startDownloadEventsPdfDocument = createAsyncThunk<
  EventPdfResponse,
  GetEventsPdfDto,
  ThunkExtra<EventsWithRootState>
>(
  'events/startDownloadEventsPdf',
  async (
    getEventsPdfDto: GetEventsPdfDto,
    { rejectWithValue, getState, dispatch, extra: { inject } },
  ) => {
    try {
      const service = inject(EventsDownloadService);
      const eventsService = inject(EventsListService);
      const format = inject(FormatService);

      const state = getState().events;

      const { total, totalDocuments } = state;

      const totalSelected = getEventsPdfDto.event_ids.length;

      let filters = state.filters;

      if (!filters.id || !filters.data) {
        filters = {
          data: await eventsService.getFilters(),
          id: '',
        };

        dispatch(setFilters(filters));
      }

      const result = await service.startDownloadEventsPdf(
        getEventsPdfDto,
        filters.data || [],
      );

      const formattedTotal = format.pluralize(
        totalSelected ? totalSelected : total,
        ['заявка', 'заявки', 'заявок'],
      );
      const formattedTotalDocuments = format.pluralize(
        totalSelected ? totalSelected : totalDocuments || 0,
        ['документ', 'документа', 'документов'],
      );

      await dispatch(
        setDownloadWaiting({
          id: result.download_id,
          count: total,
          text: {
            loading: `Создаем файл (${formattedTotal}, ${formattedTotalDocuments})`,
            complete: `Файл создан  (${formattedTotal}, ${formattedTotalDocuments})`,
            failed: 'Ошибка создания файла',
          },
        }),
      );

      dispatch(pollDownloadStatus(result));

      return result;
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const startDownloadEventsDocxDocument = createAsyncThunk<
  RequestEventDocumentDocxResponse,
  RequestEventDocumentDocxFileParams & { isMultiple: boolean },
  ThunkExtra<EventsWithRootState>
>(
  'events/startDownloadEventsDocx',
  async (
    startDownloadDocxDto,
    { rejectWithValue, dispatch, extra: { inject } },
  ) => {
    try {
      const service = inject(EventsService);
      const { event_id: eventId, isMultiple } = startDownloadDocxDto;

      const result = await service.requestEventDocumentDocxFile({
        params: { event_id: eventId },
      });

      await dispatch(
        setDownloadWaiting({
          id: result.download_id,
          count: 1,
          text: {
            loading: isMultiple
              ? 'Создаем архив (1 документ)'
              : 'Создаем файл (1 заявка, 1 документ)',
            complete: isMultiple
              ? 'Архив создан (1 документ)'
              : 'Файл создан  (1 заявка, 1 документ)',
            failed: isMultiple
              ? 'Ошибка создания архива'
              : 'Ошибка создания файла',
          },
        }),
      );

      dispatch(pollDownloadStatus(result));

      return result;
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);
